import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { s as slotFilled } from './index3.js';

function isHtmlWuiListItemElement(el) {
    return el.tagName.toLowerCase() === 'wui-list-item';
}

const getFollowingIndex = (direction, activeIdx, children, loop) => {
    // No focusable children
    if (children.every((child) => !('focusable' in child))) {
        return null;
    }
    let i = activeIdx;
    const activeChild = children[i];
    // If the current index is not focusable, we can't move anywhere
    if (i !== -1 && !(activeChild && 'focusable' in activeChild)) {
        return null;
    }
    let n = 0;
    while (n < children.length) {
        i += direction;
        if (i > children.length - 1) {
            if (loop) {
                i = 0;
            }
            else {
                return null;
            }
        }
        if (i < 0) {
            if (loop) {
                i = children.length - 1;
            }
            else {
                return null;
            }
        }
        const child = children[i];
        if (child?.focusable && !child.disabled) {
            return i;
        }
        // Prevent infinite loop
        n++;
    }
    return null;
};

const listCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:flex;flex:none;flex-direction:column;font-family:Open Sans;font-size:.875rem;font-weight:400;gap:.125rem;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;margin:0;padding:0;position:relative;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.container,.footer,.header{display:flex;flex-direction:column;gap:.125rem}.header wui-list-item-divider:last-child{padding-top:0}.footer wui-list-item-divider:first-child{padding-bottom:0}:host([scroll-size]) .container{overflow-y:auto}:host([scroll-size=xs]) .container{max-height:5.9375rem}:host([scroll-size=sm]) .container{max-height:8.3125rem}:host([scroll-size=md]) .container{max-height:10.6875rem}:host([scroll-size=lg]) .container{max-height:13.0625rem}:host([scroll-size=xl]) .container{max-height:17.8125rem}";
const WuiListStyle0 = listCss;

const WuiList = /*@__PURE__*/ proxyCustomElement(class WuiList extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.activeItemIndexChange = createEvent(this, "activeItemIndexChange", 3);
        this.scrollSize = undefined;
        this.activeItemIndex = -1;
        this.loop = true;
    }
    _activeItemIndex(newValue) {
        if (typeof newValue === 'number' && !this.isIllegalIdx(newValue)) {
            this.setActiveChild(newValue);
        }
    }
    __handleKeydown(e) {
        this._handleKeyDown(e);
    }
    // Listen for focus events on children to update own active index
    handleFocus(e) {
        const element = e.target;
        // Ensure that the focused element is a wui-list-item and its parent node is this element
        if (element.parentElement !== this.el) {
            return;
        }
        // Get the index of the focused list-item by investigating its siblings
        const focusedIdx = this.getChildElements().indexOf(element);
        this.setActiveChild(focusedIdx);
    }
    /**
     * Forward a keydown KeyboardEvent to be handled by this List component.
     *
     * Handles Enter to select/check items.
     * Handles arrow up/down to activate items (navigating through list).
     *
     * @param e KeyboardEvent (should be keydown)
     * @returns Returns true when the event was handled
     */
    async handleKeyDown(e) {
        return this._handleKeyDown(e);
    }
    async _handleKeyDown(e) {
        // ⚠️ When successfully handling a keyboard event, use `preventDefault` to stop it from doing anything else
        const activeIdx = typeof this.activeItemIndex === 'undefined' ? -1 : this.activeItemIndex;
        const childElement = this.getChildElements()[activeIdx];
        // Let wui-list-item element try to handle it
        if (childElement && isHtmlWuiListItemElement(childElement) && (await childElement.handleKeyDown(e))) {
            e.preventDefault();
            e.stopPropagation();
            return true;
        }
        let newIdx = null;
        if (e.key === 'ArrowUp') {
            newIdx = getFollowingIndex(-1, activeIdx, Array.from(this.getChildElements()), this.loop);
        }
        if (e.key === 'ArrowDown') {
            newIdx = getFollowingIndex(1, activeIdx, Array.from(this.getChildElements()), this.loop);
        }
        if (newIdx !== null && this.setActiveChild(newIdx)) {
            e.preventDefault(); // Prevent default to stop from scrolling down
            e.stopPropagation();
            return true;
        }
        return false;
    }
    getChildElements() {
        return Array.from(this.el.children);
    }
    isIllegalIdx(idx) {
        const childElements = this.getChildElements();
        // If outside boundaries
        if (!(idx >= 0 && idx < childElements.length)) {
            return true;
        }
        // Check if element exists at index
        return !childElements[idx];
    }
    setActiveChild(newIdx) {
        // Update all children
        this.getChildElements().forEach((li, idx) => {
            li.active = idx === newIdx;
            if (li.active && document.activeElement !== li) {
                li.focus();
            }
        });
        if (this.activeItemIndex !== newIdx) {
            // Update own state and emit event after updating children
            this.activeItemIndex = newIdx;
            this.activeItemIndexChange.emit(this.activeItemIndex);
        }
        return true;
    }
    async setActiveItem(item) {
        const idx = this.getChildElements().findIndex((li) => li === item);
        if (idx !== -1) {
            this.setActiveChild(idx);
        }
    }
    _handleBlur() {
        // Once we're no longer in the list, all child list items can be deactivated
        for (const li of this.getChildElements()) {
            li.active = false;
            li.blur();
        }
    }
    render() {
        return (
        // biome-ignore lint/a11y/useSemanticElements: custom element
        h(Host, { key: '0532d3fe64972ea6d6b069a8d162c0e6d24e6e3b', role: "list" }, slotFilled(this.el, 'header') && (h("div", { key: '36cb31e7142ee3ce4a7c0bf8c49e848d776810d9', class: "header" }, h("slot", { key: '31394b440d6ccdbfd44632126a0b65a5f643280d', name: "header" }))), h("div", { key: 'a007891732602fe2dec36ba639146923481bc35e', class: "container" }, h("slot", { key: '0da7e5d774afb97913cca6fa15abdc4f11e96e05' })), slotFilled(this.el, 'footer') && (h("div", { key: '5129ebd4304cc6ae44d65ddd1843a71f463260ef', class: "footer" }, h("slot", { key: '9b834a60d2c2042a74c8e7bbfd8e56aec9e756da', name: "footer" })))));
    }
    get el() { return this; }
    static get watchers() { return {
        "activeItemIndex": ["_activeItemIndex"]
    }; }
    static get style() { return WuiListStyle0; }
}, [1, "wui-list", {
        "scrollSize": [513, "scroll-size"],
        "activeItemIndex": [1026, "active-item-index"],
        "loop": [4],
        "handleKeyDown": [64],
        "setActiveItem": [64]
    }, [[0, "keydown", "__handleKeydown"], [2, "focus", "handleFocus"], [2, "blur", "_handleBlur"]], {
        "activeItemIndex": ["_activeItemIndex"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-list"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-list":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiList);
            }
            break;
    } });
}
defineCustomElement();

export { WuiList as W, defineCustomElement as d };

//# sourceMappingURL=list.js.map